import Backbone from 'backbone';
import { tags$ } from 'streams/tags/tags$';
import { Filter } from 'models/filter';
import { dynamicFilters } from 'filters/dynamicFilters';
import { isRuleVersionApplicable } from 'collections/isRuleVersionApplicable';
import {
  backboneFilterToQueryBuilderRow,
  booleanSubqueryToFilterAttributes,
  excludeMissingWorkspaceIds,
  getWorkspaceIdsInWorkspaceFilter,
} from './filterUtils';
import { computeConditionalFormatting } from './helpers/computeConditionalFormatting';
import { toColorFilterDescriptor } from './helpers/toColorFilterDescriptor';
import { FiltersAsQueryBuilderQueries } from '@ardoq/filter-interface';
import { CommonDefaultFilterKeys } from '@ardoq/filter-helpers';
import { dispatchAction } from '@ardoq/rxbeach';
import {
  notifyFilterColorChanged,
  notifyFiltersChanged,
  triggerFiltersChangedEvent,
} from 'streams/filters/FilterActions';
import {
  type APIComponentAttributes,
  APIEntityType,
  type APIFieldAttributes,
  type APIReferenceAttributes,
  ArdoqId,
  BooleanOperator,
  FilterAttributes,
  FilterTypes,
  isComponentFilter,
  isConditionalFormatting,
  isFormatting,
  isSystemFilter,
  isReferenceFilter,
  Operator,
  QueryBuilderSubquery,
} from '@ardoq/api-types';
import { calculateSystemFilters } from 'models/utils/filterUtils';
import {
  Filter as FilterBackboneModel,
  HasShouldTriggerChangeEvent,
  LoadFiltersOptions,
} from 'aqTypes';
import { notifyScenarioChanged } from 'streams/context/ContextActions';
import {
  contextReferenceId$,
  contextWorkspaceOrComponentId$,
} from 'streams/context/context$';
import { fields$ } from 'streams/fields/fields$';
import { concat, debounce, partition } from 'lodash';
import { loadedGraph$ } from 'traversals/loadedGraph$';
import { fieldValuesCache } from '../cache/FieldValuesCache';
import { resolveFieldSortedValuesFromCache } from '../cache/resolveFieldSortedValuesFromCache';
import { subscribeToAction } from 'streams/utils/streamUtils';
import { filterCache } from './filterCache';
import { maybeBackboneFiltersToPureData } from './toPureDataFilterUtils';
import {
  ReferenceAdjacency,
  type FieldValueScopeSource,
} from '@ardoq/data-model';

export type ComponentFilterData = {
  attributes: APIComponentAttributes;
  fields: APIFieldAttributes[];
  typeName: string;
  incomingRefCount: number;
  outgoingRefCount: number;
};

export type ReferenceFilterData = {
  attributes: APIReferenceAttributes;
  fields: APIFieldAttributes[];
  typeName: string;
};

type CreateFiltersFromRuleTreeArgs = {
  rules?: QueryBuilderSubquery | null;
  affectComponent: boolean;
  preexistingFilters: FilterBackboneModel[];
};

const isComponentFilterModel = ({ attributes }: FilterBackboneModel) =>
  isComponentFilter(attributes);

const isReferenceFilterModel = ({ attributes }: FilterBackboneModel) =>
  isReferenceFilter(attributes);

/**
 * Sort formatting filters by precedence.
 * System filters have the lowest priority (checked last, act as defaults).
 * Conditional formatting has higher priority (checked first, overrides defaults).
 */
const sortByFormattingPrecedence = (
  filters: FilterBackboneModel[]
): FilterBackboneModel[] =>
  concat(...partition(filters, filter => !isSystemFilter(filter.attributes)));

function getWorkspaceFilter(workspaceIds: string[]) {
  return booleanSubqueryToFilterAttributes({
    condition: BooleanOperator.OR,
    rules: workspaceIds.map(workspaceId => ({
      id: CommonDefaultFilterKeys.ROOT_WORKSPACE,
      field: CommonDefaultFilterKeys.ROOT_WORKSPACE,
      input: 'text',
      operator: Operator.EQUAL,
      type: 'string',
      value: workspaceId,
    })),
  });
}

interface FiltersCheckModelArgs {
  checkComponents?: boolean;
  checkReferences?: boolean;
}

interface NotifyOnAffectingChangeArgs extends FiltersCheckModelArgs {
  fieldNames?: string[];
}

interface HasFilterAffectedByFieldArgs extends FiltersCheckModelArgs {
  fieldName: string;
}

interface CreateFilterArgs extends HasShouldTriggerChangeEvent {
  transactionId?: string;
}

interface SetFiltersFromQueryBuilderQueriesArgs extends HasShouldTriggerChangeEvent {
  componentRules?: QueryBuilderSubquery | null;
  referenceRules?: QueryBuilderSubquery | null;
}

interface SetFormattingFiltersArgs {
  formattingRules: FilterAttributes[];
  shouldGreyOut?: boolean;
  transactionId?: string;
}

class Filters extends Backbone.Collection<FilterBackboneModel> {
  /**
   * Relates to ensure consistent views in slides, so
   * creator & viewer will get the same workspaces
   */
  workspaceFilter: FilterBackboneModel | null = null;

  notifyOnAffectingChange = debounce(
    ({
      fieldNames = [],
      checkComponents = false,
      checkReferences = false,
    }: NotifyOnAffectingChangeArgs) => {
      const isAffected = fieldNames.some(fieldName =>
        this.hasFilterAffectedByField({
          fieldName,
          checkComponents,
          checkReferences,
        })
      );

      if (isAffected) {
        dispatchAction(triggerFiltersChangedEvent());
      }
    },
    50
  );

  transactionId?: string;

  constructor() {
    super(undefined, { model: Filter });
    subscribeToAction(notifyFiltersChanged, () => {
      // Resetting cache so that we get correct caching of filters.
      filterCache.reset();
      dispatchAction(notifyFilterColorChanged());
    });

    const resetFieldValuesCache = () => fieldValuesCache.reset();
    loadedGraph$.subscribe(resetFieldValuesCache);
    fields$.subscribe(resetFieldValuesCache);

    const handleContextChanged = () => {
      if (this.models.some(filter => filter.isDynamic())) {
        dispatchAction(triggerFiltersChangedEvent());
      }
    };

    contextWorkspaceOrComponentId$.subscribe(handleContextChanged);
    contextReferenceId$.subscribe(handleContextChanged);

    subscribeToAction(notifyScenarioChanged, handleContextChanged);

    tags$.subscribe(() => {
      const tagFilters = this.getTagFilters() || [];
      if (tagFilters.length > 0) {
        dispatchAction(triggerFiltersChangedEvent());
      }
    });
  }

  set(models: FilterBackboneModel[], options?: Backbone.CollectionSetOptions) {
    this.transactionId = undefined;
    return super.set(models, options);
  }

  getPersistedFilters() {
    return maybeBackboneFiltersToPureData({
      advancedFilters: this.filter(filter => filter !== this.workspaceFilter),
      workspaceFilter: this.workspaceFilter,
    });
  }

  loadFilters({
    workspaceFilter,
    advancedFilters = [],
    shouldTriggerChangeEvent,
  }: LoadFiltersOptions) {
    this.removeAllFilters({ shouldTriggerChangeEvent: false });
    if (workspaceFilter) {
      this.workspaceFilter = this.createFilter(
        excludeMissingWorkspaceIds(workspaceFilter),
        { shouldTriggerChangeEvent: false }
      );
    }
    advancedFilters.forEach(filter => {
      this.createFilter(filter, { shouldTriggerChangeEvent: false });
    });
    if (shouldTriggerChangeEvent) {
      dispatchAction(triggerFiltersChangedEvent());
    }
  }

  removeConditionalFormattingFilters({
    shouldTriggerChangeEvent,
  }: HasShouldTriggerChangeEvent) {
    this.getConditionalFormattingFilters().forEach(filter => {
      this.removeFilter(filter, { shouldTriggerChangeEvent: false });
    });
    if (shouldTriggerChangeEvent) {
      dispatchAction(triggerFiltersChangedEvent());
    }
  }

  removeReferenceLabelFormattingFilters({
    shouldTriggerChangeEvent,
  }: HasShouldTriggerChangeEvent) {
    const referenceLabelFormattingFilters = this.models.filter(
      ({ attributes }) => attributes.type === FilterTypes.REFERENCE_LABEL
    );
    referenceLabelFormattingFilters.forEach(model => this.remove(model));
    if (shouldTriggerChangeEvent) {
      dispatchAction(triggerFiltersChangedEvent());
    }
  }

  removeComponentLabelFormattingFilters({
    shouldTriggerChangeEvent,
  }: HasShouldTriggerChangeEvent) {
    const referenceLabelFormattingFilters = this.models.filter(
      ({ attributes }) => attributes.type === FilterTypes.COMPONENT_LABEL
    );
    referenceLabelFormattingFilters.forEach(model => this.remove(model));
    if (shouldTriggerChangeEvent) {
      dispatchAction(triggerFiltersChangedEvent());
    }
  }

  removeIrrelevantWorkspaceFilters(connectedWorkspaceIds: ArdoqId[]) {
    // Irrelevant workspace filters are workspaces that aren't
    // connected to the ones that we have open
    const workspaceIds = this.getWorkspaceIdsInWorkspaceFilter();
    if (workspaceIds.some(wsId => !connectedWorkspaceIds.includes(wsId))) {
      this.updateWorkspaceFilter(
        workspaceIds.filter(wsId => connectedWorkspaceIds.includes(wsId))
      );
    }
  }

  clearWorkspaceFilter({
    shouldTriggerChangeEvent,
  }: HasShouldTriggerChangeEvent) {
    const workspaceFilter = this.workspaceFilter;
    if (workspaceFilter) {
      this.workspaceFilter = null;
      this.removeFilter(workspaceFilter, {
        shouldTriggerChangeEvent,
      });
    }
  }

  ensureWorkspaceInWorkspaceFilter(workspaceId: ArdoqId) {
    const workspaceIds = this.getWorkspaceIdsInWorkspaceFilter();
    if (!workspaceIds.length || workspaceIds.includes(workspaceId)) {
      return;
    }
    this.updateWorkspaceFilter([...workspaceIds, workspaceId]);
  }

  getWorkspaceIdsInWorkspaceFilter() {
    return this.workspaceFilter
      ? getWorkspaceIdsInWorkspaceFilter(this.workspaceFilter)
      : [];
  }

  updateWorkspaceFilter(workspaceIds: string[]) {
    this.clearWorkspaceFilter({ shouldTriggerChangeEvent: false });
    const hasWorkspaceIds = workspaceIds && workspaceIds.length > 0;
    if (hasWorkspaceIds)
      this.workspaceFilter = this.createFilter(
        getWorkspaceFilter(Array.from(new Set(workspaceIds))),
        { shouldTriggerChangeEvent: false }
      );
    dispatchAction(triggerFiltersChangedEvent());
  }

  createWorkspaceFilterFromURL(attributes: string) {
    this.workspaceFilter = this.createFilter(attributes, {
      shouldTriggerChangeEvent: true,
    });
    return this.workspaceFilter;
  }

  createFilter(
    attributes: FilterBackboneModel | FilterAttributes | string | null,
    options: CreateFilterArgs = {}
  ) {
    const { shouldTriggerChangeEvent, transactionId, ...restOptions } = options;
    const filter = this.createFilterFromRule(attributes, restOptions);
    this.add(filter);
    filterCache.reset();

    if (shouldTriggerChangeEvent) {
      dispatchAction(triggerFiltersChangedEvent());
    }
    this.transactionId = transactionId;
    return filter;
  }

  createFilterFromRule(
    attributes: FilterBackboneModel | FilterAttributes | string | null,
    options: unknown = {}
  ) {
    const filter =
      attributes instanceof Filter
        ? attributes
        : new Filter(attributes, options);
    const rules: FilterBackboneModel[] = filter.get('rules');
    if (rules) {
      const filterFromRules = rules.map(rule =>
        this.createFilterFromRule(rule)
      );
      if (filter.get('type') === FilterTypes.DATE_RANGE) {
        filter.setRules(filterFromRules);
      } else {
        filter.set('rules', filterFromRules);
      }
    }
    return filter;
  }

  removeFilter(
    filter: FilterBackboneModel,
    options: HasShouldTriggerChangeEvent = {}
  ) {
    const { shouldTriggerChangeEvent, ...restOptions } = options;
    this.remove(filter, restOptions);
    if (shouldTriggerChangeEvent) {
      dispatchAction(triggerFiltersChangedEvent());
    }
  }

  removeAllFilters({ shouldTriggerChangeEvent }: HasShouldTriggerChangeEvent) {
    this.reset();
    this.workspaceFilter = null;

    if (shouldTriggerChangeEvent) {
      dispatchAction(triggerFiltersChangedEvent());
    }
  }

  getAllFilters() {
    return this.models?.filter(filter =>
      isRuleVersionApplicable(filter.attributes.version)
    );
  }

  getFiltersByType(type: FilterTypes, options?: never) {
    const parsedOptions: { type?: FilterTypes } = options || {};
    parsedOptions.type = type;
    return this.where(parsedOptions);
  }

  getAttributeFilters(options?: never) {
    return this.getFiltersByType(FilterTypes.ATTRIBUTE, options);
  }

  getDateRangeFilters(options?: never) {
    return this.getFiltersByType(FilterTypes.DATE_RANGE, options);
  }

  getTagFilters(options?: never) {
    return this.getFiltersByType(FilterTypes.TAG, options);
  }

  getCompLabelFilter(options?: never) {
    return this.getFiltersByType(FilterTypes.COMPONENT_LABEL, options).filter(
      filter => !filter.get('affectsTypeName' satisfies keyof FilterAttributes)
    );
  }

  getRefLabelFilter(options?: never) {
    return this.getFiltersByType(FilterTypes.REFERENCE_LABEL, options).filter(
      filter => !filter.get('affectsTypeName' satisfies keyof FilterAttributes)
    );
  }

  getCompMultiLabelFilter(options?: never) {
    return this.getFiltersByType(FilterTypes.COMPONENT_LABEL, options).filter(
      filter => filter.get('affectsTypeName' satisfies keyof FilterAttributes)
    );
  }

  getRefMultiLabelFilter(options?: never) {
    return this.getFiltersByType(FilterTypes.REFERENCE_LABEL, options).filter(
      filter => filter.get('affectsTypeName' satisfies keyof FilterAttributes)
    );
  }

  getFiltersAsQueryBuilderQueries(): FiltersAsQueryBuilderQueries {
    const filtersWithoutWorkspaceFilter = this.getAllFilters().filter(
      filter => filter !== this.workspaceFilter
    );
    return {
      componentRules: {
        condition: BooleanOperator.AND,
        rules: filtersWithoutWorkspaceFilter
          .filter(isComponentFilterModel)
          .map(backboneFilterToQueryBuilderRow),
      },
      referenceRules: {
        condition: BooleanOperator.AND,
        rules: filtersWithoutWorkspaceFilter
          .filter(isReferenceFilterModel)
          .map(backboneFilterToQueryBuilderRow),
      },
    };
  }

  /**
   * The goal is to enable OR condition the top level in perspectives query builder.
   * https://ardoqcom.atlassian.net/browse/ARD-13495
   * But the top level condition internally must be AND because of multiple negative
   * filters and backward compatibility, as it is used across the app today.
   *
   * The idea is to "submerse" the user one (rule) level down in the perspectives
   * (ignore the ruleRoot level if its rules contain only one rule with OR condition) and
   * let the query builder do its work. If there are multiple rules, we must use AND logic.
   * Therefore, if there is a top level OR condition, I create one filter from the rule
   * root in setFiltersFromQueryBuilderQueries(), instead of one filter for every rule.
   * This way, if we add negative filters (by excluding stuff from the view), we still
   * have a logically correct rule tree, where the OR root becomes a subquery.
   */

  createFiltersFromRuleTree({
    rules,
    affectComponent,
    preexistingFilters,
  }: CreateFiltersFromRuleTreeArgs) {
    if (rules) {
      if (rules.condition === BooleanOperator.OR) {
        this.createFilter(
          booleanSubqueryToFilterAttributes(rules, affectComponent)!
        );
      } else {
        booleanSubqueryToFilterAttributes(
          rules,
          affectComponent
        )!.rules!.forEach(filter =>
          this.createFilter(filter, { shouldTriggerChangeEvent: false })
        );
      }
    } else if (rules !== null) {
      preexistingFilters.forEach(filter => this.add(filter));
    }
  }

  setConditionalFormattingFilters({
    formattingRules,
    shouldGreyOut = true,
    transactionId,
  }: SetFormattingFiltersArgs) {
    this.removeConditionalFormattingFilters({
      shouldTriggerChangeEvent: false,
    });

    const attributeFormattingRules = formattingRules.filter(
      rule => isRuleVersionApplicable(rule.version) && !isSystemFilter(rule)
    );

    const systemFilters = calculateSystemFilters({
      conditionalFormattingRules: attributeFormattingRules,
      shouldGreyOut: Boolean(shouldGreyOut),
    });

    const applicableRules = [...attributeFormattingRules, ...systemFilters];
    applicableRules.forEach(filter => this.add(filter));
    this.transactionId = transactionId;
    // this results in dispatching notifyFilterChange action.
    // perspectiveEditorFormattingOptions$ listens for this action and whenever triggered,
    // refreshes applied conditional formatting in Perspective Editor
    // (It's needed for change detection in Perspective Editor)
    dispatchAction(triggerFiltersChangedEvent());
  }

  setFiltersFromQueryBuilderQueries({
    componentRules,
    referenceRules,
    shouldTriggerChangeEvent,
  }: SetFiltersFromQueryBuilderQueriesArgs) {
    const formattingFilters = this.getFormattingFilters();
    const filters = this.getAllFilters();
    const componentFilters = filters.filter(isComponentFilterModel);
    const referenceFilters = filters.filter(isReferenceFilterModel);
    this.reset();
    filterCache.reset();
    if (this.workspaceFilter) {
      this.add(this.workspaceFilter);
    }
    formattingFilters.forEach(filter => this.add(filter));

    this.createFiltersFromRuleTree({
      rules: componentRules,
      affectComponent: true,
      preexistingFilters: componentFilters,
    });
    this.createFiltersFromRuleTree({
      rules: referenceRules,
      affectComponent: false,
      preexistingFilters: referenceFilters,
    });
    if (shouldTriggerChangeEvent) {
      dispatchAction(triggerFiltersChangedEvent());
    }
  }

  _getNestedFiltersLeafNodeCount(filters: FilterBackboneModel[]): number {
    return filters
      .map(({ attributes }) =>
        attributes.rules && attributes.type !== FilterTypes.DATE_RANGE
          ? this._getNestedFiltersLeafNodeCount(attributes.rules)
          : 1
      )
      .reduce((sum, i) => sum + i, 0);
  }

  getTotalFilterCount() {
    return this.getFilterCount() + this.getFormattingCount();
  }

  /** Returns the count of active filters. Note: this does not include formatting filters. */
  getFilterCount() {
    return this._getNestedFiltersLeafNodeCount(this.getIncludeFilters());
  }

  getFormattingCount() {
    return this.getFormattingFilters().length;
  }

  getIncludeFilters() {
    return this.models.filter(({ attributes }) => !isFormatting(attributes));
  }

  getFormattingFilters() {
    return this.models.filter(({ attributes }) => isFormatting(attributes));
  }

  getConditionalFormattingFilters() {
    return this.models.filter(({ attributes }) =>
      isConditionalFormatting(attributes)
    );
  }

  hasFilterAffectedByField({
    fieldName,
    checkComponents = false,
    checkReferences = false,
  }: HasFilterAffectedByFieldArgs) {
    return this.getAttributeFilters().some(filter => {
      const passesComponentCheck =
        !checkComponents || filter.get('affectComponent');
      const passesReferenceCheck =
        !checkReferences || filter.get('affectReference');
      return (
        filter.get('name') === fieldName &&
        passesComponentCheck &&
        passesReferenceCheck
      );
    });
  }

  getFilterColors() {
    return this.filter(f => f.isComponentFormatting()).map<string>(f =>
      f.get('color')
    );
  }

  getFilterReferenceColors() {
    return this.filter(f => f.isReferenceFormatting()).map<string>(f =>
      f.get('color')
    );
  }

  /**
   * Gets the first matching color filter for a component or reference.
   * Returns null if no matching color filter is found.
   */
  getFirstMatchingColorFilter(
    entityType: APIEntityType,
    attributes: APIComponentAttributes | APIReferenceAttributes,
    fields: APIFieldAttributes[],
    referenceAdjacency: ReferenceAdjacency,
    referenceTypeName: string | null
  ): FilterBackboneModel | null {
    const filters = this.getAllFilters();
    const sortedFilters = sortByFormattingPrecedence(filters);

    for (const filter of sortedFilters) {
      if (
        filter.get('color') &&
        filter.isIncluded(
          entityType,
          attributes,
          fields,
          referenceAdjacency,
          referenceTypeName
        ) &&
        filter.isAffected(entityType)
      ) {
        return filter;
      }
    }

    return null;
  }

  getCachedFilterResult(id: ArdoqId): boolean | undefined {
    return filterCache.isIncluded(id);
  }

  private applyConditionalFormattingAndCache(
    scopeSource: FieldValueScopeSource | null,
    entityType: APIEntityType.COMPONENT | APIEntityType.REFERENCE,
    attributes: APIComponentAttributes | APIReferenceAttributes,
    fields: APIFieldAttributes[],
    included: boolean,
    typeName: string,
    incomingRefCount: number,
    outgoingRefCount: number,
    referenceAdjacency: ReferenceAdjacency
  ): boolean {
    const referenceTypeName =
      entityType === APIEntityType.REFERENCE ? typeName : null;
    const firstColorFilter = this.getFirstMatchingColorFilter(
      entityType,
      attributes,
      fields,
      referenceAdjacency,
      referenceTypeName
    );

    const { returnValue, cacheWrite } = computeConditionalFormatting({
      entityType,
      attributes,
      included,
      typeName,
      incomingRefCount,
      outgoingRefCount,
      colorFilter: firstColorFilter
        ? toColorFilterDescriptor(firstColorFilter)
        : null,
      resolveFieldSortedValues: (
        filterAttributeName,
        resolveEntityType,
        affectsTypeName,
        affectsTypeNames
      ) =>
        resolveFieldSortedValuesFromCache(
          scopeSource,
          filterAttributeName,
          resolveEntityType,
          affectsTypeName,
          affectsTypeNames
        ),
    });

    if (cacheWrite) {
      filterCache.set(
        attributes._id,
        cacheWrite.included,
        cacheWrite.color,
        cacheWrite.version
      );
    }

    return returnValue;
  }

  isComponentIncludedInContextByFilter(
    scopeSource: FieldValueScopeSource | null,
    component: ComponentFilterData,
    referenceAdjacency: ReferenceAdjacency,
    forceReloadCache = false
  ): boolean {
    const { attributes, fields } = component;
    const id = attributes._id;

    const cachedResult = forceReloadCache
      ? undefined
      : filterCache.isIncluded(id);
    if (cachedResult !== undefined) {
      return cachedResult;
    }

    if (forceReloadCache) {
      filterCache.unset(id);
    }

    const filters = this.getAllFilters();

    const includedByFilters = filters
      .filter(({ attributes }) => !isFormatting(attributes))
      .every(filter =>
        filter.isIncluded(
          APIEntityType.COMPONENT,
          attributes,
          fields,
          referenceAdjacency,
          null
        )
      );

    const included = includedByFilters && dynamicFilters.includesComponent(id);

    return this.applyConditionalFormattingAndCache(
      scopeSource,
      APIEntityType.COMPONENT,
      attributes,
      fields,
      included,
      component.typeName,
      component.incomingRefCount,
      component.outgoingRefCount,
      referenceAdjacency
    );
  }

  isReferenceIncludedInContextByFilter(
    scopeSource: FieldValueScopeSource | null,
    reference: ReferenceFilterData,
    source: ComponentFilterData,
    target: ComponentFilterData,
    referenceAdjacency: ReferenceAdjacency,
    forceReloadCache = false
  ): boolean {
    const { attributes, fields } = reference;
    const id = attributes._id;

    const cachedResult = forceReloadCache
      ? undefined
      : filterCache.isIncluded(id);
    if (cachedResult !== undefined) {
      return cachedResult;
    }

    if (forceReloadCache) {
      filterCache.unset(id);
    }

    const filters = this.getAllFilters();

    const includedByFilters = filters
      .filter(({ attributes }) => !isFormatting(attributes))
      .every(filter =>
        filter.isIncluded(
          APIEntityType.REFERENCE,
          attributes,
          fields,
          referenceAdjacency,
          reference.typeName
        )
      );

    const included =
      includedByFilters &&
      dynamicFilters.includesReference(attributes) &&
      this.isComponentIncludedInContextByFilter(
        scopeSource,
        source,
        referenceAdjacency,
        forceReloadCache
      ) &&
      this.isComponentIncludedInContextByFilter(
        scopeSource,
        target,
        referenceAdjacency,
        forceReloadCache
      );

    return this.applyConditionalFormattingAndCache(
      scopeSource,
      APIEntityType.REFERENCE,
      attributes,
      fields,
      included,
      reference.typeName,
      0,
      0,
      referenceAdjacency
    );
  }
}

const globalFilters = new Filters();
export { globalFilters };
